import { Component, computed, input, output } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { DateFormatPipe } from '@processpuzzle/base-entity';
import { Plan, Subscription, SubscriptionStatus } from '@processpuzzle/platform-admin';

/**
 * What the customer is subscribed to, and until when.
 *
 * **The card's central judgement is that cancelling is not the same as cancelled.** A subscription
 * with `cancelAtPeriodEnd` set is still `ACTIVE`: the customer has paid for the running period and
 * is owed it, so service continues to `currentPeriodEnd`. A card that read only `status` would tell
 * a customer who had just cancelled that nothing had happened, and a card that read only
 * `cancelAtPeriodEnd` would tell a customer whose period had elapsed that they were still running.
 * {@link cancelling} and {@link ended} are the two states that fall out of reading both.
 *
 * **It offers Cancel or Resume, never both.** They are the two halves of one cancellation resource,
 * and showing both would ask the customer to work out which applies.
 *
 * The price is `plan.price` off the model — the formatted string — never `amountMinor`. The integer
 * is the currency's minor unit, so rendering it raw would show "4900" for forty-nine euros, and
 * dividing it here would be a second place money arithmetic happens.
 */
@Component({
  selector: 'pp-subscription-status-card',
  imports: [MatCard, MatCardTitle, MatCardContent, MatButton, MatIcon, TranslocoDirective, DateFormatPipe],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.portal.status'">
      <mat-card class="status-card" test-id="portal-status-card">
        <mat-card-title>{{ t('title') }}</mat-card-title>
        <mat-card-content>
          @if (subscription(); as current) {
            <dl class="status-grid">
              <dt>{{ t('plan') }}</dt>
              <dd test-id="portal-status-plan">{{ plan()?.name ?? current.planCode }}</dd>

              @if (plan(); as currentPlan) {
                <dt>{{ t('price') }}</dt>
                <dd test-id="portal-status-price">{{ currentPlan.price }} / {{ t('interval.' + currentPlan.interval) }}</dd>
              }

              <dt>{{ t('seats') }}</dt>
              <dd test-id="portal-status-seats">{{ current.quantity }}</dd>

              <dt>{{ t('state') }}</dt>
              <dd test-id="portal-status-state">
                @if (ended()) {
                  <span class="state-ended">{{ t('states.ended') }}</span>
                } @else if (cancelling()) {
                  <!-- "Cancels on <date>", not "cancelled": service is still running. -->
                  <span class="state-cancelling">{{ t('states.cancelling', { date: current.currentPeriodEnd | ppDate }) }}</span>
                } @else {
                  <span class="state-running">{{ t('states.' + current.status) }}</span>
                }
              </dd>

              <dt>{{ t('period') }}</dt>
              <dd test-id="portal-status-period">{{ current.currentPeriodStart | ppDate }} – {{ current.currentPeriodEnd | ppDate }}</dd>

              @if (current.trialEndsAt) {
                <dt>{{ t('trialEnds') }}</dt>
                <dd test-id="portal-status-trial">{{ current.trialEndsAt | ppDate }}</dd>
              }
            </dl>

            @if (cancelling()) {
              <p class="status-cancelling-note" test-id="portal-cancelling-note">
                <mat-icon>schedule</mat-icon>
                <span>{{ t('cancellingNote', { date: current.currentPeriodEnd | ppDate }) }}</span>
              </p>
              <button id="portal-resume" type="button" mat-raised-button color="primary" test-id="portal-resume" [disabled]="busy()" (click)="resume.emit()">
                {{ t('resume') }}
              </button>
            } @else if (!ended()) {
              <button id="portal-cancel" type="button" mat-button test-id="portal-cancel" [disabled]="busy()" (click)="cancel.emit()">
                {{ t('cancel') }}
              </button>
            }
          } @else {
            <!-- A tenant bootstrapped rather than bought: a real state, and a different thing to say
                 than "we could not load your subscription". -->
            <p test-id="portal-status-none">{{ t('none') }}</p>
          }
        </mat-card-content>
      </mat-card>
    </ng-container>
  `,
  styles: [
    `
      .status-card {
        max-width: 560px;
      }

      .status-grid {
        display: grid;
        grid-template-columns: auto 1fr;
        column-gap: 16px;
        row-gap: 4px;
        margin: 0 0 12px;
      }

      .status-grid dt {
        font-weight: 600;
        opacity: 0.75;
      }

      .status-grid dd {
        margin: 0;
      }

      .state-cancelling {
        color: var(--mat-sys-error, #b3261e);
      }

      .status-cancelling-note {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        font-size: 0.9rem;
      }
    `,
  ],
})
export class SubscriptionStatusCardComponent {
  readonly subscription = input<Subscription | undefined>(undefined);
  readonly plan = input<Plan | undefined>(undefined);
  /** Disables both actions while a write is in flight, so neither can be issued twice. */
  readonly busy = input(false);

  readonly cancel = output<void>();
  readonly resume = output<void>();

  /** Cancelled, and the period has elapsed: nothing left to cancel or resume. */
  readonly ended = computed(() => this.subscription()?.status === SubscriptionStatus.CANCELED);

  /** Cancelled, and still running. The state the card exists to render correctly. */
  readonly cancelling = computed(() => !!this.subscription()?.cancelAtPeriodEnd && !this.ended());
}
